import React, { useState, useEffect } from "react";
import { IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SuiAvatar from "components/SuiAvatar";
import PropTypes from "prop-types";
import SuiBox from "components/SuiBox";
import BucketApi from "api/bucketApi";
import useStyles from "../styles";
import { useAuth } from "../../../../../auth-context/auth.context";

const ProfileImage = ({ onProfileImageChange }) => {
  const { user, profileImage: contextProfileImage, setUser } = useAuth();
  const [localProfileImage, setLocalProfileImage] = useState(contextProfileImage);
  const [loadingImage, setLoadingImage] = useState(false);
  const [imageError, setImageError] = useState(false);
  const classes = useStyles();

  const fileInputRef = React.createRef();

  useEffect(() => {
    setLocalProfileImage(contextProfileImage);
  }, [contextProfileImage]);

  const handleImageUpload = async (event) => {
    if (!event.target.files[0]) return;
    setLoadingImage(true);
    try {
      await BucketApi.uploadProfileImage(event.target.files[0]);
      const imageResponse = await BucketApi.getProfileImageUrl(user._id);
      const newImageUrl = imageResponse.data.file_url;

      // Actualizar el estado local inmediatamente
      setLocalProfileImage(newImageUrl);

      // Actualizar el contexto global
      setUser((prevUser) => ({ ...prevUser, profileImage: newImageUrl }));

      onProfileImageChange();
      setImageError(false);
    } catch (error) {
      console.error("Error uploading profile image:", error);
      setImageError(true);
    } finally {
      setLoadingImage(false);
    }
  };

  const handleImageDelete = async () => {
    if (window.confirm("¿Estás seguro(a) de que deseas eliminar la imagen de perfil?")) {
      try {
        await BucketApi.deleteProfileImage();
        setLocalProfileImage(null);
        setUser((prevUser) => ({ ...prevUser, profileImage: null }));
        onProfileImageChange();
      } catch (error) {
        console.error("Error deleting profile image:", error);
        setImageError(true);
      }
    }
  };

  const handleImageError = () => {
    setImageError(true);
    setLocalProfileImage(null);
  };

  const getInitials = () => {
    if (user?.nombres && user?.apellidos) {
      return `${user.nombres[0]}${user.apellidos[0]}`.toUpperCase();
    }
    return user?.email?.[0].toUpperCase() || "";
  };

  const renderAvatar = () => {
    return (
      <SuiAvatar
        variant="rounded"
        size="xl"
        customClass="shadow-sm"
        style={{
          backgroundColor: "#E0E0E0",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#333",
          fontWeight: "medium",
          position: "relative",
        }}
      >
        {getInitials()}
        {localProfileImage && !imageError && !loadingImage && (
          <img
            src={localProfileImage}
            alt="profile"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
            onError={handleImageError}
          />
        )}
      </SuiAvatar>
    );
  };

  return (
    <SuiBox
      position="relative"
      display="inline-block"
      className={classes.profileImageContainer}
      style={{ width: "90px", height: "90px", borderRadius: "12px", overflow: "hidden" }}
    >
      {renderAvatar()}

      <IconButton
        className={classes.editButton}
        style={{
          position: "absolute",
          top: "4px",
          left: "4px",
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          borderRadius: "50%",
          border: "1px solid rgba(0, 0, 0, 0.1)",
          padding: "2px",
          zIndex: 2,
        }}
        onClick={() => fileInputRef.current.click()}
      >
        <EditIcon fontSize="small" />
      </IconButton>

      <IconButton
        className={classes.deleteButton}
        style={{
          position: "absolute",
          top: "4px",
          right: "4px",
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          borderRadius: "50%",
          border: "1px solid rgba(0, 0, 0, 0.1)",
          padding: "2px",
          zIndex: 2,
        }}
        onClick={handleImageDelete}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>

      <input
        ref={fileInputRef}
        id="profileImageInput"
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleImageUpload}
      />
    </SuiBox>
  );
};

ProfileImage.propTypes = {
  onProfileImageChange: PropTypes.func.isRequired,
};

export default ProfileImage;
